import {utils} from '@/kuTools/sdk/utils';
import {EntryFile, EntryFileType} from './constant';
import type {
    ILockBuilderResponse,
    IGetBuilderLockResponse,
    IGetTemplateListResponse,
    IGetTemplateResponse,
    IUnlockBuilderResponse,
    IGetResourceListResponse,
} from './request.type';

/**
 * 创建 builder
 *
 * @param docGuid 文档 id
 * @param builderId 复制场景需传入，基于该 builderId 创建新的（可选）
 * @returns
 */
export const createBuilder = async (
    docGuid: string,
    builderId?: string,
    templateId?: string
) => {
    return utils.rteRequest({
        url: '/daas/api/builder',
        method: 'post',
        data: {
            docId: docGuid,
            sourceId: builderId,
            templateId: templateId,
        },
    });
};

// 获取 builder 存储数据
export const getBuilderData = async (builderId: string) => {
    return utils.rteRequest({
        url: `/daas/api/builder/${builderId}`,
        method: 'get',
    });
};

// 更新 builder 存储数据
export const updateBuilderData = async (
    builderId: string,
    options: { code: string, compiledCode: string, dependencies: string[], style: string },
    templateId?: string
) => {
    const {code, compiledCode, dependencies, style} = options;

    return utils.rteRequest({
        url: `/daas/api/builder/${builderId}`,
        method: 'post',
        data: {
            content: [
                {
                    type: EntryFileType,
                    code,
                    name: EntryFile,
                },
            ],
            output: {
                [EntryFile]: {
                    type: EntryFileType,
                    code: compiledCode,
                    dependencies,
                    style,
                },
            },
            templateId,
        },
    });
};

/**
 * 获取聊天记录列表
 *
 * @param builderId
 * @param chatId
 * @returns
 */
export const getChatMessageList = async (builderId: string, chatId: string) => {
    return utils.rteRequest({
        url: `/daas/api/chat/${builderId}/${chatId}/messages`,
        method: 'get',
    });
};

/**
* 停止指定聊天会话生成
*
* @param builderId
* @param chatId 聊天会话ID
*/
export const stopChat = async (
    builderId: string,
    chatId: string,
    artifactId?: string,
    command?: string,
    based?: string
) => {
    return utils.rteRequest({
        url: `/daas/api/chat/${builderId}/${chatId}/stop`,
        method: 'post',
        data: {
            artifactId,
            command,
            based,
        },
    });
};

/**
 * 查询模板列表
 * @param limit 模板数量限制，后端默认 200
 */
export const getTemplateList = async (limit?: string): Promise<IGetTemplateListResponse> => {
    return utils.rteRequest({
        url: '/daas/api/template',
        method: 'get',
        params: {
            // 默认200
            limit,
        },
    });
};

/**
 * 带缓存的查询模板列表
 * 在下一次刷新页面前，只会发起一次请求
 */
export const getTemplateListCache = (() => {
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let cache: Promise<IGetTemplateListResponse> | undefined;
    return async (...args: any): Promise<IGetTemplateListResponse> => {
        if (cache) {
            return cache;
        }

        try {
            cache = getTemplateList(...args);
            return cache;
        } catch (error: any) {
            return Promise.reject(error);
        }
    };
})();

/**
 * 查询模板详情
 * @param templateId 模板 id
 */
export const getTemplate = async (templateId: string): Promise<IGetTemplateResponse | undefined> => {
    if (!templateId) {
        return;
    }
    return utils.rteRequest({
        url: `/daas/api/template/${templateId}`,
        method: 'get',
    });
};

/**
 * 查询编辑锁
 * @param builderId builder id
 */
export const getBuilderLock = async (builderId: string): Promise<IGetBuilderLockResponse | undefined> => {
    if (!builderId) {
        return;
    }
    return utils.rteRequest({
        url: `/daas/api/builder/${builderId}/lock`,
        method: 'get',
    });
};

/**
 * 创建编辑锁
 * @param builderId builder id
 */
export const lockBuilder = async (builderId: string): Promise<ILockBuilderResponse | undefined> => {
    if (!builderId) {
        return;
    }
    return utils.rteRequest({
        url: `/daas/api/builder/${builderId}/lock`,
        method: 'post',
    });
};

/**
 * 删除编辑锁
 * @param builderId builder id
 */
export const unlockBuilder = async (builderId: string): Promise<IUnlockBuilderResponse | undefined> => {
    if (!builderId) {
        return;
    }
    return utils.rteRequest({
        url: `/daas/api/builder/${builderId}/unlock`,
        method: 'post',
    });
};

// 获取 builder 编辑状态数据（全屏编辑模式）
export const getBuilderEditState = async (builderId: string) => {
    return utils.rteRequest({
        url: `/daas/api/builder/${builderId}/state`,
        method: 'get',
    });
};

// 更新 Artifact 代码
export const updateArtifactCode = async ({
    builderId,
    chatId,
    code,
    artifactId,
    title,
    artifactIdentify,
}: {
    builderId: string;
    chatId: string;
    code: string;
    artifactId: string;
    title: string;
    artifactIdentify: string;
}) => {
    if (!artifactId) {
        return;
    }

    return utils.rteRequest({
        url: `/daas/api/artifact/${artifactId}`,
        method: 'post',
        data: {
            builderId,
            chatId,
            content: [
                {
                    type: EntryFileType,
                    code,
                    name: EntryFile,
                    title,
                    id: artifactIdentify,
                },
            ],
        },
    });
};

// 获取 Artifact 代码
export const getArtifactCode = async (artifactId: string) => {
    return utils.rteRequest({
        url: `/daas/api/artifact/${artifactId}`,
        method: 'get',
    });
};

// 发布 Artifact
export const publishArtifact = async ({
    artifactId,
    builderId,
    chatId,
    code,
    compiledCode,
    dependencies,
    style,
    title,
    artifactIdentify,
}: {
    artifactId: string;
    builderId: string;
    chatId: string;
    code: string;
    compiledCode: string;
    dependencies: string[];
    style: string;
    title: string;
    artifactIdentify: string;
}) => {
    return utils.rteRequest({
        url: `/daas/api/artifact/${artifactId}/publish`,
        method: 'post',
        data: {
            builderId,
            chatId,
            content: [
                {
                    type: EntryFileType,
                    code,
                    name: EntryFile,
                    title,
                    id: artifactIdentify,
                },
            ],
            output: {
                [EntryFile]: {
                    type: EntryFileType,
                    code: compiledCode,
                    dependencies,
                    style,
                },
            },
            title,
        },
    });
};

// 获取 builder KV 数据
export const getBuilderKV = async (builderId: string) => {
    return utils.rteRequest({
        url: `/daas/api/storage/${builderId}/keys/count`,
        method: 'get',
    });
};

// 更新KV数据
export const saveBuilderKV = async (builderId: string, key: string, value: any) => {
    return utils.rteRequest({
        url: `/daas/api/storage/${builderId}`,
        method: 'post',
        data: {
            key,
            value,
        },
    });
};

// 编译代码
export const compileCode = async (builderId: string, code: string) => {
    if (!code) {
        return;
    }

    return utils.rteRequest({
        url: `/daas/api/compile/${builderId}`,
        method: 'post',
        data: {
            code,
            style: true, // 默认启用样式编译
        },
    });
};

/**
 * 查询资源列表
 */
export const getResourceList = async (): Promise<IGetResourceListResponse> => {
    return utils.rteRequest({
        url: '/daas/api/resource',
        method: 'get',
    });
};
